import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UploadFileItem, UploadManagerService, UploadStatus } from '@doc/api/server-bo';
import { UploadMediaDialogService } from '../services/upload-media-dialog.service';
import { runUploadControlAction, uploadControlLoadingKey } from '../shared/upload-control-loading';
import { Subject, takeUntil } from 'rxjs';

interface FolderUploadIndicatorSummary {
  folderName: string;
  fileIds: string[];
  totalItems: number;
  uploadedItems: number;
  status: UploadStatus;
  isComplete: boolean;
}

@Component({
  selector: 'app-upload-indicator',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './upload-indicator.component.html',
  styleUrls: ['./upload-indicator.component.scss']
})
export class UploadIndicatorComponent implements OnInit, OnDestroy {
  activeUploadsCount: number = 0;
  uploads: UploadFileItem[] = [];
  folderUploads: FolderUploadIndicatorSummary[] = [];
  standaloneFiles: UploadFileItem[] = [];
  isCollapsed: boolean = false;
  isDismissed: boolean = false;
  readonly controlActionLoadingKeys = new Set<string>();
  private destroy$ = new Subject<void>();
  private uploadManager = inject(UploadManagerService);
  private uploadDialogService = inject(UploadMediaDialogService);

  constructor(
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.uploadManager.uploads$
      .pipe(takeUntil(this.destroy$))
      .subscribe(uploads => {
        const visibleUploads = uploads.filter(upload => upload.status !== 'cancelled');
        this.uploads = visibleUploads;
        this.folderUploads = this.buildFolderSummaries(visibleUploads);
        this.standaloneFiles = visibleUploads
          .filter(upload => !this.getTopFolderName(upload))
          .sort((a, b) => a.file.name.localeCompare(b.file.name));
        this.activeUploadsCount = visibleUploads.length;

        const hasInFlightUploads = visibleUploads.some(upload => {
          const status = this.getNormalizedStatus(upload);
          return status === 'uploading' || status === 'queued' || status === 'initializing' || status === 'paused';
        });

        if (hasInFlightUploads) {
          this.isDismissed = false;
        }
        if (visibleUploads.length === 0) {
          this.isCollapsed = false;
          this.isDismissed = false;
        }

        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasActiveUploads(): boolean {
    return this.activeUploadsCount > 0 && !this.isDismissed;
  }

  get inProgressCount(): number {
    return this.uploads.filter(upload => {
      const status = this.getNormalizedStatus(upload);
      return status !== 'completed' && status !== 'failed' && status !== 'cancelled';
    }).length;
  }

  get headerTitle(): string {
    if (this.inProgressCount > 0) {
      const suffix = this.inProgressCount === 1 ? '' : 's';
      return `Uploading ${this.inProgressCount} item${suffix}`;
    }
    return 'Uploads complete';
  }

  toggleCollapsed(event: MouseEvent): void {
    event.stopPropagation();
    this.isCollapsed = !this.isCollapsed;
  }

  dismissIndicator(event: MouseEvent): void {
    event.stopPropagation();
    this.isDismissed = true;
  }

  getFileStatusText(upload: UploadFileItem): string {
    const status = this.getFileStatus(upload);
    if (status === 'completed') return 'Uploaded';
    if (status === 'failed') return 'Failed';
    if (status === 'paused') return 'Paused';
    if (status === 'uploading' || status === 'initializing') return 'Uploading...';
    return 'Pending...';
  }

  getFolderStatusText(folder: FolderUploadIndicatorSummary): string {
    if (folder.isComplete) {
      return 'Upload complete';
    }
    if (folder.status === 'failed') {
      return 'Failed';
    }
    if (folder.status === 'paused') {
      return `Paused... ${folder.uploadedItems} of ${folder.totalItems} items`;
    }
    if (folder.status === 'uploading' || folder.status === 'initializing') {
      return `Uploading... ${folder.uploadedItems} of ${folder.totalItems} items`;
    }
    return `${folder.uploadedItems} of ${folder.totalItems} items`;
  }

  isFileCompleted(upload: UploadFileItem): boolean {
    return this.getFileStatus(upload) === 'completed';
  }

  isFileFailed(upload: UploadFileItem): boolean {
    return this.getFileStatus(upload) === 'failed';
  }

  isFilePaused(upload: UploadFileItem): boolean {
    return this.getFileStatus(upload) === 'paused';
  }

  isFolderCompleted(folder: FolderUploadIndicatorSummary): boolean {
    return folder.isComplete;
  }

  isFolderFailed(folder: FolderUploadIndicatorSummary): boolean {
    return folder.status === 'failed';
  }

  isFolderPaused(folder: FolderUploadIndicatorSummary): boolean {
    return folder.status === 'paused';
  }

  canPauseFile(upload: UploadFileItem): boolean {
    return ['uploading', 'initializing', 'queued'].includes(upload.status);
  }

  canResumeFile(upload: UploadFileItem): boolean {
    return upload.status === 'paused' && !this.isResumeBlockedForUpload(upload);
  }

  showDisabledResumeFile(upload: UploadFileItem): boolean {
    return upload.status === 'paused' && this.isResumeBlockedForUpload(upload);
  }

  canPauseFolder(folder: FolderUploadIndicatorSummary): boolean {
    return folder.fileIds.some(fileId => {
      const upload = this.uploadManager.getUpload(fileId);
      if (!upload) return false;
      return ['uploading', 'initializing', 'queued'].includes(upload.status);
    });
  }

  canResumeFolder(folder: FolderUploadIndicatorSummary): boolean {
    return !this.isResumeBlockedForFolder(folder) && folder.fileIds.some(fileId => {
      const upload = this.uploadManager.getUpload(fileId);
      return !!upload && upload.status === 'paused';
    });
  }

  showDisabledResumeFolder(folder: FolderUploadIndicatorSummary): boolean {
    const hasPausedItems = folder.fileIds.some(fileId => {
      const upload = this.uploadManager.getUpload(fileId);
      return !!upload && upload.status === 'paused';
    });
    return hasPausedItems && this.isResumeBlockedForFolder(folder);
  }

  isControlActionLoading(key: string): boolean {
    return this.controlActionLoadingKeys.has(key);
  }

  pauseFileUpload(event: MouseEvent, upload: UploadFileItem): void {
    event.stopPropagation();
    void runUploadControlAction(
      this.controlActionLoadingKeys,
      uploadControlLoadingKey.filePause(upload.fileId),
      () => this.cdr.detectChanges(),
      () => {
        if (upload.status === 'completed' || upload.status === 'cancelled' || upload.status === 'failed') {
          return;
        }
        this.uploadManager.pauseUpload(upload.fileId);
      },
      () => this.uploadManager.getUpload(upload.fileId)?.status === 'paused'
    );
  }

  resumeFileUpload(event: MouseEvent, upload: UploadFileItem): void {
    event.stopPropagation();
    if (upload.status !== 'paused' || this.isResumeBlockedForUpload(upload)) {
      return;
    }
    void runUploadControlAction(
      this.controlActionLoadingKeys,
      uploadControlLoadingKey.fileResume(upload.fileId),
      () => this.cdr.detectChanges(),
      () => this.uploadManager.resumeUpload(upload.fileId),
      () => this.isFileResumeSettled(upload.fileId),
      { timeoutMs: 2000 }
    );
  }

  pauseFolderUpload(event: MouseEvent, folder: FolderUploadIndicatorSummary): void {
    event.stopPropagation();
    void runUploadControlAction(
      this.controlActionLoadingKeys,
      uploadControlLoadingKey.folderPause(folder.folderName),
      () => this.cdr.detectChanges(),
      () => {
        folder.fileIds.forEach(fileId => {
          const item = this.uploadManager.getUpload(fileId);
          if (!item) return;
          if (item.status === 'completed' || item.status === 'cancelled' || item.status === 'failed') {
            return;
          }
          this.uploadManager.pauseUpload(fileId);
        });
      },
      () => this.isFolderPaused(folder)
    );
  }

  resumeFolderUpload(event: MouseEvent, folder: FolderUploadIndicatorSummary): void {
    event.stopPropagation();
    if (this.isResumeBlockedForFolder(folder)) {
      return;
    }
    void runUploadControlAction(
      this.controlActionLoadingKeys,
      uploadControlLoadingKey.folderResume(folder.folderName),
      () => this.cdr.detectChanges(),
      () => {
        const pausedFileIds = folder.fileIds.filter(fileId => {
          const item = this.uploadManager.getUpload(fileId);
          return !!item && item.status === 'paused' && !this.isResumeBlockedForUpload(item);
        });
        this.uploadManager.resumePausedUploads(pausedFileIds);
      },
      () => this.isFolderResumeSettled(folder),
      { timeoutMs: 2000 }
    );
  }

  private isFileResumeSettled(fileId: string): boolean {
    const upload = this.uploadManager.getUpload(fileId);
    if (!upload) {
      return true;
    }
    if (upload.status === 'uploading' || upload.status === 'initializing') {
      return true;
    }
    if (upload.status === 'queued' && upload.sessionId) {
      return true;
    }
    return upload.status === 'paused';
  }

  private isFolderResumeSettled(folder: FolderUploadIndicatorSummary): boolean {
    if (folder.status === 'uploading' || folder.status === 'initializing' || folder.status === 'queued') {
      return true;
    }
    return folder.status === 'paused';
  }

  trackFolder(_: number, folder: FolderUploadIndicatorSummary): string {
    return folder.folderName;
  }

  trackFile(_: number, upload: UploadFileItem): string {
    return upload.fileId;
  }

  /**
   * Open upload dialog when indicator is clicked
   */
  openUploadDialog(): void {
    const firstUpload = this.uploads.find(upload => upload.status !== 'cancelled');
    const folderId = firstUpload?.metadata?.folderId || 0;
    this.uploadDialogService.openDialog(0, folderId);
  }

  private getNormalizedStatus(upload: UploadFileItem): UploadStatus {
    if (upload.status === 'queued' && upload.progress > 0 && upload.sessionId) {
      return 'uploading';
    }
    if (upload.status === 'queued' && upload.progress > 0 && !upload.sessionId) {
      return 'paused';
    }
    return upload.status;
  }

  private getFileStatus(upload: UploadFileItem): UploadStatus {
    return this.getNormalizedStatus(upload);
  }

  private getRelativeUploadPath(upload: UploadFileItem): string {
    const relativePath = (
      (upload.file as any)?.__currentRelativePath ||
      (upload.file as any)?.webkitRelativePath ||
      ''
    ).replace(/\\/g, '/').trim();
    return relativePath || upload.file.name;
  }

  private isResumeBlockedForUpload(upload: UploadFileItem): boolean {
    return !!upload.file && (upload.file as any).__isPlaceholder === true;
  }

  private isResumeBlockedForFolder(folder: FolderUploadIndicatorSummary): boolean {
    const pausedUploads = folder.fileIds
      .map(fileId => this.uploadManager.getUpload(fileId))
      .filter((upload): upload is UploadFileItem => !!upload && this.getFileStatus(upload) === 'paused');

    if (pausedUploads.length === 0) {
      return false;
    }

    return pausedUploads.every(upload => this.isResumeBlockedForUpload(upload));
  }

  private getTopFolderName(upload: UploadFileItem): string | null {
    const relativePath = this.getRelativeUploadPath(upload);
    const pathParts = relativePath.split('/').filter(Boolean);
    if (pathParts.length <= 1) {
      return null;
    }
    return pathParts[0];
  }

  private getFolderAggregateStatus(statusCounts: Record<UploadStatus, number>, totalItems: number): UploadStatus {
    if (statusCounts.completed === totalItems && totalItems > 0) return 'completed';
    if (statusCounts.cancelled === totalItems && totalItems > 0) return 'cancelled';
    if (statusCounts.uploading > 0 || statusCounts.initializing > 0) return 'uploading';
    if (statusCounts.paused > 0) return 'paused';
    if (statusCounts.queued > 0) return 'queued';
    if (statusCounts.failed > 0) return 'failed';
    return 'queued';
  }

  private buildFolderSummaries(uploads: UploadFileItem[]): FolderUploadIndicatorSummary[] {
    const folderMap = new Map<string, {
      fileIds: string[];
      totalItems: number;
      uploadedItems: number;
      statusCounts: Record<UploadStatus, number>;
    }>();

    uploads.forEach(upload => {
      const folderName = this.getTopFolderName(upload);
      if (!folderName) {
        return;
      }
      if (!folderMap.has(folderName)) {
        folderMap.set(folderName, {
          fileIds: [],
          totalItems: 0,
          uploadedItems: 0,
          statusCounts: {
            queued: 0,
            initializing: 0,
            uploading: 0,
            paused: 0,
            completed: 0,
            failed: 0,
            cancelled: 0
          }
        });
      }

      const current = folderMap.get(folderName)!;
      const status = this.getNormalizedStatus(upload);
      current.fileIds.push(upload.fileId);
      current.totalItems += 1;
      current.statusCounts[status] += 1;
      if (status === 'completed') {
        current.uploadedItems += 1;
      }
    });

    return Array.from(folderMap.entries())
      .map(([folderName, data]) => ({
        folderName,
        fileIds: data.fileIds,
        totalItems: data.totalItems,
        uploadedItems: data.uploadedItems,
        status: this.getFolderAggregateStatus(data.statusCounts, data.totalItems),
        isComplete: data.totalItems > 0 && data.uploadedItems === data.totalItems
      }))
      .sort((a, b) => a.folderName.localeCompare(b.folderName));
  }
}
